import { apiClient } from '../../../core/api/client';
import {
  SearchFilters,
  SearchProvidersResponse,
  UserRole,
} from '../types';
import type { ProviderProfile } from '../types/provider-profile.types';
import { ensureAnonSessionId } from '@/shared/utils/anonymousSession';

/**
 * Search providers with filters
 * 
 * Implements FR-018 through FR-029:
 * - Multi-select taxonomy filtering (categories, specialties, expertise)
 * - Geographic search with radius (converted from miles to km on backend)
 * - Service range filtering (IN_HOME, VIRTUAL, IN_OFFICE)
 * - Pagination support
 * - Automatic filtering of APPROVED + ACTIVE providers (FR-121)
 * - Financial Advisor self-exclusion (FR-124)
 * 
 * @param filters - Search and filter criteria
 * @returns Paginated list of providers matching criteria
 */
export const searchProviders = async (
  filters: SearchFilters
): Promise<SearchProvidersResponse> => {
  const response = await apiClient.get<SearchProvidersResponse>(
    '/search/providers',
    {
      params: filters,
      headers: { 'x-anon-session-id': ensureAnonSessionId() },
    }
  );
  // Pre-login (Item 1): the backend returns PublicSearchProvidersResponseDto for unauthenticated
  // callers, which omits email / calendlyUrl / lat / lng / isFavorited / isInNetwork. Normalize
  // here so downstream components can rely on the full ProviderSearchResult shape.
  return {
    ...response.data,
    providers: response.data.providers.map((p) => ({
      ...p,
      calendlyUrl: p.calendlyUrl ?? null,
      latitude: p.latitude ?? null,
      longitude: p.longitude ?? null,
      isFavorited: p.isFavorited ?? false,
      isInNetwork: p.isInNetwork ?? false,
      user: {
        ...p.user,
        email: p.user.email ?? '',
      },
    })),
  };
};

/**
 * Get provider details by ID
 * 
 * Retrieves complete provider profile including:
 * - Business information and contact details
 * - Categories, specialties, and expertise
 * - Service areas and coverage
 * - Calendly scheduling URL
 * 
 * Note: Only APPROVED providers with ACTIVE accounts are accessible (FR-121)
 * 
 * @param id - Provider ID (ServiceProvider.id UUID)
 * @returns Provider details
 * @throws 404 if provider not found or not accessible
 */
export const getProviderById = async (
  id: string
): Promise<ProviderProfile> => {
  const response = await apiClient.get<ProviderProfile>(
    `/providers/${id}`
  );
  return response.data;
};

/**
 * Build query parameters from search filters
 * Helper function to construct URL search params for local filter persistence
 * 
 * @param filters - Search filters
 * @returns URLSearchParams object
 */
export const buildSearchParams = (filters: SearchFilters): URLSearchParams => {
  const params = new URLSearchParams();

  if (filters.page) params.append('page', filters.page.toString());
  if (filters.limit) params.append('limit', filters.limit.toString());
  if (filters.providerType) params.append('providerType', filters.providerType);
  
  if (filters.categoryIds) {
    filters.categoryIds.forEach(id => params.append('categoryIds', id));
  }
  
  if (filters.specialtyIds) {
    filters.specialtyIds.forEach(id => params.append('specialtyIds', id));
  }
  
  if (filters.expertiseIds) {
    filters.expertiseIds.forEach(id => params.append('expertiseIds', id));
  }
  
  if (filters.serviceRanges) {
    filters.serviceRanges.forEach(range => params.append('serviceRanges', range));
  }
  
  if (filters.newClientAcceptance !== undefined) {
    params.append('newClientAcceptance', filters.newClientAcceptance.toString());
  }
  
  if (filters.excludeRole) {
    params.append('excludeRole', filters.excludeRole);
  }
  
  if (filters.latitude !== undefined) {
    params.append('latitude', filters.latitude.toString());
  }
  
  if (filters.longitude !== undefined) {
    params.append('longitude', filters.longitude.toString());
  }
  
  if (filters.radiusMiles !== undefined) {
    params.append('radiusMiles', filters.radiusMiles.toString());
  }
  
  if (filters.stateCode) {
    params.append('stateCode', filters.stateCode);
  }
  
  if (filters.countyId) {
    params.append('countyId', filters.countyId);
  }

  return params;
};

/**
 * Parse search filters from URL search params
 * Helper function to restore filters from browser URL
 * 
 * @param params - URLSearchParams from URL
 * @returns SearchFilters object
 */
export const parseSearchParams = (params: URLSearchParams): SearchFilters => {
  const filters: SearchFilters = {};

  const page = params.get('page');
  if (page) filters.page = parseInt(page, 10);

  const limit = params.get('limit');
  if (limit) filters.limit = parseInt(limit, 10);

  const providerType = params.get('providerType');
  if (providerType === 'SERVICE_PROVIDER' || providerType === 'FINANCIAL_ADVISOR') {
    filters.providerType = providerType;
  }

  const categoryIds = params.getAll('categoryIds');
  if (categoryIds.length > 0) filters.categoryIds = categoryIds;

  const specialtyIds = params.getAll('specialtyIds');
  if (specialtyIds.length > 0) filters.specialtyIds = specialtyIds;

  const expertiseIds = params.getAll('expertiseIds');
  if (expertiseIds.length > 0) filters.expertiseIds = expertiseIds;

  const serviceRanges = params.getAll('serviceRanges');
  if (serviceRanges.length > 0) {
    filters.serviceRanges = serviceRanges as SearchFilters['serviceRanges'];
  }

  const newClientAcceptance = params.get('newClientAcceptance');
  if (newClientAcceptance) {
    filters.newClientAcceptance = newClientAcceptance === 'true';
  }

  const excludeRole = params.get('excludeRole');
  if (excludeRole) filters.excludeRole = excludeRole as UserRole;

  const latitude = params.get('latitude');
  if (latitude) filters.latitude = parseFloat(latitude);

  const longitude = params.get('longitude');
  if (longitude) filters.longitude = parseFloat(longitude);

  const radiusMiles = params.get('radiusMiles');
  if (radiusMiles) filters.radiusMiles = parseInt(radiusMiles, 10);

  const stateCode = params.get('stateCode');
  if (stateCode) filters.stateCode = stateCode;

  const countyId = params.get('countyId');
  if (countyId) filters.countyId = countyId;

  return filters;
};

